import {
  WINDOW_NOTIFICATION_SHOW,
  WINDOW_NOTIFICATION_HIDE,
  ROAD_LANE_FEATURE_SET_ENDING,
  ROAD_LANE_FEATURE_ADD_POINT_MANUALLY,
  ROAD_LANE_POINT_CHANGED,
  ROAD_LANE_FEATURE_DELETE_POINT
} from './mutations.type';
import {
  BEGIN_ROAD_LANE_ANALYSIS,
  STOP_ROAD_LANE_ANALYSIS,
  ROAD_LANE_FEATURE_ADD_POINT,
  ROAD_LANE_FEATURE_END_DRAWING,
  ROAD_LANE_GENERATE_REPORT
} from './actions.type';

import Feature from 'ol/Feature';
import { LineString, Point } from 'ol/geom';
import { Modify } from 'ol/interaction';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import GeoJSONFormat from 'ol/format/GeoJSON';
import { point, lineString } from '@turf/helpers';
import LineSplit from '@turf/line-split';
import { getLength } from 'ol/sphere';
import { Style, Text, Stroke, Fill, Circle as CircleStyle } from 'ol/style';
import { Api as AnalysisApi } from '../api/api-analizy';
import { Api as ApiReport } from '../api/api-raporty';
import ContextInstance from '../api/ContextInstance';
import SettingsInstance from '../api/SettingsInstance';

const state = {
  started: false,
  drawingEnded: false,
  drawnFeature: null,
  mapLayer: null,
  modifyInteraction: null,
  pointAdded: false,
  segments: []
};

const actions = {
  [BEGIN_ROAD_LANE_ANALYSIS]({ state, commit, rootGetters }) {
    const source = new VectorSource();
    state.drawnFeature = new Feature();
    source.addFeature(state.drawnFeature);
    state.mapLayer = new VectorLayer({
      source: source,
      zIndex: Infinity,
      style: new Style({
        stroke: new Stroke({
          color: '#2A35CC',
          width: 1
        })
      })
    });

    const convertToGeojson = new GeoJSONFormat();
    const clickTolerance = 20;

    state.modifyInteraction = new Modify({
      source: source,
      pixelTolerance: clickTolerance,
      condition: function(evt) {
        const featureToSplit = source.getClosestFeatureToCoordinate(
          evt.coordinate
        );
        const featureToSplitGeometry = featureToSplit.getGeometry().clone();
        const closestCoordsOnFeature = featureToSplitGeometry.getClosestPoint(
          evt.coordinate
        );
        const closestPointOnFeature = new Point(closestCoordsOnFeature);
        const dist = getLength(
          new LineString([evt.coordinate, closestCoordsOnFeature])
        );
        const pixelDistance = dist / rootGetters.resolution;

        if (pixelDistance <= clickTolerance) {
          const turfLine = lineString(
            featureToSplitGeometry
              .transform(rootGetters.coordinateSystem, 'EPSG:4326')
              .getCoordinates()
          );
          const pointToSplitBy = point(
            closestPointOnFeature
              .transform(rootGetters.coordinateSystem, 'EPSG:4326')
              .getCoordinates()
          );
          const sliced = LineSplit(turfLine, pointToSplitBy);

          if (sliced.features.length > 1) {
            const firstSlicedFeature = convertToGeojson.readFeature(
              sliced.features[0],
              {
                featureProjection: rootGetters.coordinateSystem,
                dataProjection: 'EPSG:4326'
              }
            );
            firstSlicedFeature.odcinek = featureToSplit.odcinek;

            const secondSlicedFeature = convertToGeojson.readFeature(
              sliced.features[1],
              {
                featureProjection: rootGetters.coordinateSystem,
                dataProjection: 'EPSG:4326'
              }
            );
            secondSlicedFeature.odcinek = firstSlicedFeature.odcinek + 1;

            firstSlicedFeature.kategoria_drogi = secondSlicedFeature.kategoria_drogi =
              featureToSplit.kategoria_drogi;

            source.getFeatures().forEach(f => {
              if (f.odcinek >= secondSlicedFeature.odcinek) {
                f.odcinek += 1;
              }
            });

            source.removeFeature(featureToSplit);

            source.addFeature(firstSlicedFeature);
            source.addFeature(secondSlicedFeature);

            state.segments = source
              .getFeatures()
              .sort((first, second) => first.odcinek - second.odcinek);
          }
        }

        return false;
      }
    });

    commit(WINDOW_NOTIFICATION_SHOW, {
      type: 'info',
      title: 'Informacja.',
      message:
        'Narysuj planowany przebieg inwestycji. Aby zakończyć wybierz dwukrotnie koniec linii.',
      duration: -1
    });

    state.started = true;
  },
  [STOP_ROAD_LANE_ANALYSIS]({ state, commit }) {
    commit(WINDOW_NOTIFICATION_HIDE);

    state.started = state.drawingEnded = state.pointAdded = false;
    state.drawnFeature = null;
    state.segments = [];
  },
  [ROAD_LANE_FEATURE_ADD_POINT]({ state, dispatch }, pointCoordinates) {
    if (!state.started || state.drawingEnded) {
      return;
    }

    if (state.pointAdded) {
      dispatch(ROAD_LANE_FEATURE_END_DRAWING);
    } else {
      const featureGeometry = state.drawnFeature.getGeometry();
      if (featureGeometry) {
        const coordinates = featureGeometry.getCoordinates();
        const lastPoint = coordinates[coordinates.length - 2];

        if (
          lastPoint[0] != pointCoordinates[0] ||
          lastPoint[1] != pointCoordinates[1]
        ) {
          state.pointAdded = true;
        }
      } else {
        state.drawnFeature.setGeometry(new LineString([pointCoordinates]));
        state.pointAdded = true;
      }
    }
  },
  [ROAD_LANE_FEATURE_END_DRAWING]({ state, commit }) {
    if (!state.drawingEnded) {
      const drawnFeatureGeometry = state.drawnFeature.getGeometry();
      if (
        drawnFeatureGeometry &&
        drawnFeatureGeometry.getCoordinates().length >= 2
      ) {
        state.drawingEnded = true;
        state.drawnFeature.odcinek = 1;
        state.drawnFeature.kategoria_drogi = null;
        state.segments.push(state.drawnFeature);

        const whiteFill = new Fill({ color: 'white' });
        const whiteStroke = new Stroke({ color: 'white', width: 2 });

        const blackFill = new Fill({ color: '#000000' });
        const blackStroke = new Stroke({ color: '#000000', width: 2 });

        state.mapLayer.setStyle(function(feature) {
          const featureGeometry = feature.getGeometry();
          const styles = [
            new Style({ stroke: new Stroke({ color: '#2A35CC', width: 1 }) })
          ];

          const firstCoordinate = featureGeometry.getFirstCoordinate();
          styles.push(
            new Style({
              geometry: new Point(firstCoordinate),
              image: new CircleStyle({
                fill: blackFill,
                stroke: blackStroke,
                radius: 2
              })
            })
          );

          const lastCoordinate = featureGeometry.getLastCoordinate();
          styles.push(
            new Style({
              geometry: new Point(lastCoordinate),
              image: new CircleStyle({
                fill: blackStroke,
                stroke: blackStroke,
                radius: 2
              })
            })
          );

          styles.push(
            new Style({
              geometry: featureGeometry,
              text: new Text({
                text: feature.odcinek.toString(),
                scale: 2,
                placement: 'point',
                fill: blackStroke,
                textBaseline: 'bottom'
              })
            })
          );

          return styles;
        });

        commit(WINDOW_NOTIFICATION_HIDE);
        commit(WINDOW_NOTIFICATION_SHOW, {
          type: 'info',
          title: 'Informacja.',
          message:
            'Podziel planowaną inwestycję na odcinki ze względu na kategorię drogi.',
          duration: -1
        });
      } else {
        commit(WINDOW_NOTIFICATION_SHOW, {
          type: 'warn',
          title: 'Uwaga.',
          message:
            'Aby zakończyć rysowanie obiekt musi posiadać co najmniej dwie współrzędne.',
          duration: 5000
        });
      }
    }
  },
  async [ROAD_LANE_GENERATE_REPORT]({ state, rootGetters, commit }) {
    state.segments.forEach(s => {
      s.getGeometry()
        .clone()
        .transform(rootGetters.coordinateSystem, 'EPSG:2180');
    });

    const body = {
      features: state.segments.map(s => ({
        geometry: {
          coordinates: s.getGeometry().getCoordinates(),
          type: s.getGeometry().getType()
        },
        type: 'Feature',
        kategoria_drogi: s.kategoria_drogi,
        odcinek: s.odcinek.toString()
      })),
      type: 'FeatureCollection'
    };

    commit(WINDOW_NOTIFICATION_SHOW, {
      type: 'info',
      title: 'Informacja',
      message:
        'Trwa generacja raportu. Po zakończeniu raport wyświetli się automatycznie oraz będzie dostępny w magazynie raportów.',
      duration: 5000
    });

    const api = new AnalysisApi();
    api
      .postAnalizyZajeciePasaDrogowegoAnaliza({
        wso2_id: this.contextEmail ? this.contextEmail : null,
        orgid: this.contextOrganizationId ? this.contextOrganizationId : null,
        body: body
      })
      .then(async function(response) {
        if (response.body.raport) {
          const reportId = response.body.raport.id;

          const reportsApi = new ApiReport();

          let intervalId = setTimeout(async function askForStatus() {
            const control = Date.now().toString();
            const result = await reportsApi.getRaportyById({
              control: control,
              id: reportId
            });

            if (result && result.body && result.body.control == control) {
              if (result.body.raporty.status.value == 'generated') {
                const downloadUrl = `${SettingsInstance.baseApiUrl}/raporty/by_uuid/${result.body.raporty.uuid}`;
                window.open(downloadUrl, '_blank');
              } else {
                intervalId = setTimeout(askForStatus, 10000);
              }
            }
          }, 0);
        }
      })
      .catch(function(response) {
        if (
          response &&
          response.body &&
          response.body.error &&
          response.body.error == 'Brak danych'
        ) {
          commit(WINDOW_NOTIFICATION_SHOW, {
            type: 'error',
            title: 'Błąd.',
            message:
              'W systemie nie ma wystarczających danych aby przeprowadzić analizę.',
            duration: 5000
          });
        } else {
          commit(WINDOW_NOTIFICATION_SHOW, {
            type: 'error',
            title: 'Uwaga.',
            message: 'Analiza nie powiodła się.',
            duration: 5000
          });
        }
      });
  }
};

const mutations = {
  [ROAD_LANE_FEATURE_SET_ENDING](state, endPointCoordinates) {
    if (!state.started || state.drawingEnded) {
      return;
    }

    const featureGeometry = state.drawnFeature.getGeometry();

    if (featureGeometry) {
      if (state.pointAdded) {
        featureGeometry.appendCoordinate(endPointCoordinates);
        state.pointAdded = false;
      } else {
        const coordinates = featureGeometry.getCoordinates();
        coordinates[coordinates.length - 1] = endPointCoordinates;
        featureGeometry.setCoordinates(coordinates);
      }
    }
  },
  [ROAD_LANE_FEATURE_ADD_POINT_MANUALLY](state, pointCoordinates) {
    if (!state.started || state.drawingEnded) {
      return;
    }

    const featureGeometry = state.drawnFeature.getGeometry();

    if (featureGeometry) {
      featureGeometry.appendCoordinate(pointCoordinates);
    } else {
      state.drawnFeature.setGeometry(new LineString([pointCoordinates]));
    }

    state.pointAdded = true;
  },
  [ROAD_LANE_POINT_CHANGED](state, { index, coordinates }) {
    const featureGeometry = state.drawnFeature.getGeometry();
    const featureCoordinates = featureGeometry.getCoordinates();
    featureCoordinates[index] = coordinates;
    featureGeometry.setCoordinates(featureCoordinates);
  },
  [ROAD_LANE_FEATURE_DELETE_POINT](state, index) {
    const drawnFeatureCoordinates = state.drawnFeature
      .getGeometry()
      .getCoordinates();
    drawnFeatureCoordinates.splice(index, 1);
    state.drawnFeature.getGeometry().setCoordinates(drawnFeatureCoordinates);
    state.pointAdded = true;
  }
};

const getters = {
  roadLaneAnalysisStarted: state => state.started,
  roadLaneAnalysisDrawingEnded: state => state.drawingEnded,
  roadLaneAnalysisLayer: state => state.mapLayer,
  roadLaneAnalysisModifyInteraction: state => state.modifyInteraction,
  roadLaneSegments: state => state.segments,
  roadLaneFeature: state => state.drawnFeature
};

export default {
  state,
  actions,
  mutations,
  getters
};
